import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Cpu, Layers, FileText, Globe } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAccessToken } from '@/auth/useAccessToken';
import { ticketsApi } from './ticketsApi';
import { createTicketSchema, type CreateTicketInput, type TicketCategory } from './schema';
import { useActiveApplications } from '@/features/applications/useApplications';
import { useAttachmentUploads } from '@/features/attachments/useAttachmentUploads';
import { AttachmentDropzone } from '@/features/attachments/AttachmentDropzone';
import { attachmentsApi } from '@/features/attachments/attachmentsApi';
import { RichTextEditor } from './RichTextEditor';
import type { ApiError } from '@/lib/apiClient';
import { useState } from 'react';

/**
 * Submit-ticket form. Mirrors the layout from aes-helpdesk-mockup.html.
 *
 * Stack: react-hook-form + Zod for validation + TanStack Query for the
 * mutation lifecycle. The plain-text body is a temporary stand-in — Lexical
 * arrives in slice #2 (PostMessage / RichTextBody).
 */
const categories: { value: TicketCategory; label: string; icon: typeof Cpu }[] = [
  { value: 'Hardware', label: 'Hardware', icon: Cpu },
  { value: 'Application', label: 'Application', icon: Layers },
  { value: 'Reports', label: 'Reports', icon: FileText },
  { value: 'Networking', label: 'Networking', icon: Globe }
];

export function CreateTicketForm() {
  const getAccessToken = useAccessToken();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // The application options are admin-managed (see /admin/applications), fetched
  // from the API rather than hard-coded so IT can change them without a deploy.
  const applications = useActiveApplications();

  const form = useForm<CreateTicketInput>({
    resolver: zodResolver(createTicketSchema),
    defaultValues: {
      subject: '',
      // No category preselected — the user must choose one before submitting.
      category: undefined,
      application: undefined,
      priority: 'Normal',
      bodyText: ''
    }
  });

  const attachments = useAttachmentUploads();
  // Rich body (carries inline-image <img> tags) + inline image attachment ids.
  const [body, setBody] = useState({ json: '', html: '' });
  const [inlineImageIds, setInlineImageIds] = useState<string[]>([]);
  const [editorReset, setEditorReset] = useState(0);
  const [inlinePending, setInlinePending] = useState(0);

  const submitMutation = useMutation({
    mutationFn: (input: CreateTicketInput) =>
      ticketsApi.create(input, getAccessToken, {
        attachmentIds: [...attachments.ids, ...inlineImageIds],
        bodyJson: body.json,
        bodyHtml: body.html
      }),
    onSuccess: (response) => {
      attachments.reset();
      setInlineImageIds([]);
      setEditorReset((n) => n + 1);
      // Bust the cached "my tickets" list so /tickets shows the new entry
      // immediately on navigation. Once we have a /tickets/:n detail route
      // we'll deep-link to it instead of bouncing to the list.
      void queryClient.invalidateQueries({ queryKey: ['tickets', 'mine'] });
      void navigate('/tickets', {
        replace: true,
        state: { justCreated: response.ticketNumber }
      });
    }
  });

  const category = form.watch('category');
  const subject = form.watch('subject') ?? '';

  return (
    <form
      onSubmit={form.handleSubmit((data) => submitMutation.mutate(data))}
      className="p-4 sm:p-6 lg:p-8 max-w-5xl mx-auto space-y-5"
    >
      <header>
        <h1 className="text-2xl font-semibold">Submit a Ticket</h1>
        <p className="text-sm text-muted-foreground mt-1">
          We aim to acknowledge new tickets within 30 minutes during business hours.
        </p>
      </header>

      {submitMutation.isError && (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {(submitMutation.error as ApiError)?.body
            ? formatProblem((submitMutation.error as ApiError).body)
            : (submitMutation.error as Error).message}
        </div>
      )}

      <div className="rounded-xl border bg-card p-5">
        <label className="block text-xs uppercase tracking-wider text-muted-foreground mb-2">Category</label>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {categories.map(({ value, label, icon: Icon }) => {
            const active = category === value;
            return (
              <label
                key={value}
                className={cn(
                  'cursor-pointer rounded-md p-3 text-center border transition',
                  active ? 'border-primary bg-primary/10' : 'border-border hover:border-primary/40'
                )}
              >
                <input type="radio" value={value} {...form.register('category')} className="sr-only" />
                <Icon className="w-5 h-5 mx-auto" />
                <div className="text-sm mt-1">{label}</div>
              </label>
            );
          })}
        </div>
        {form.formState.errors.category && (
          <p className="mt-2 text-xs text-destructive">{form.formState.errors.category.message}</p>
        )}

        {category === 'Application' && (
          <div className="mt-4">
            <label htmlFor="application" className="block text-xs uppercase tracking-wider text-muted-foreground mb-2">
              Which application?
            </label>
            <select
              id="application"
              {...form.register('application')}
              disabled={applications.isPending}
              className="w-full bg-background border rounded-md px-3 py-2 text-sm outline-none focus:ring-2 ring-primary/40 disabled:opacity-60"
            >
              <option value="">
                {applications.isPending ? 'Loading applications…' : '— Select an application —'}
              </option>
              {applications.data?.map((a) => (
                <option key={a.id} value={a.name}>
                  {a.name}
                </option>
              ))}
            </select>
            {form.formState.errors.application && (
              <p className="mt-1 text-xs text-destructive">{form.formState.errors.application.message}</p>
            )}
          </div>
        )}
      </div>

      <div className="rounded-xl border bg-card p-5">
        <label htmlFor="subject" className="block text-xs uppercase tracking-wider text-muted-foreground mb-2">
          Subject
        </label>
        <input
          id="subject"
          {...form.register('subject')}
          maxLength={200}
          placeholder="Brief description of the issue"
          className="w-full bg-background border rounded-md px-3 py-2.5 text-sm outline-none focus:ring-2 ring-primary/40"
        />
        <div className="mt-1 text-xs text-muted-foreground">{subject.length} / 200</div>
        {form.formState.errors.subject && (
          <p className="mt-1 text-xs text-destructive">{form.formState.errors.subject.message}</p>
        )}
      </div>

      <div className="rounded-xl border bg-card p-5">
        <label htmlFor="bodyText" className="block text-xs uppercase tracking-wider text-muted-foreground mb-2">
          Describe the issue
        </label>
        <RichTextEditor
          resetSignal={editorReset}
          minHeight={160}
          placeholder="What happened, what you expected, any error messages, what you've already tried. Paste a screenshot to embed it."
          onChange={(c) => {
            form.setValue('bodyText', c.bodyText, { shouldValidate: form.formState.isSubmitted });
            setBody({ json: c.bodyJson, html: c.bodyHtml });
          }}
          uploadImage={async (file) => {
            setInlinePending((n) => n + 1);
            try {
              const up = await attachmentsApi.upload(file, true, getAccessToken);
              return { id: up.id, url: up.url };
            } finally {
              setInlinePending((n) => n - 1);
            }
          }}
          onInlineImageAdded={(id) => setInlineImageIds((prev) => [...prev, id])}
        />
        {form.formState.errors.bodyText && (
          <p className="mt-1 text-xs text-destructive">{form.formState.errors.bodyText.message}</p>
        )}
        <AttachmentDropzone
          items={attachments.items}
          onAddFiles={attachments.addFiles}
          onRemove={attachments.remove}
          disabled={submitMutation.isPending}
        />
      </div>

      <div className="rounded-xl border bg-card p-5">
        <label className="block text-xs uppercase tracking-wider text-muted-foreground mb-2">Priority</label>
        <div className="flex gap-2 text-sm">
          {(['Low', 'Normal', 'High', 'Urgent'] as const).map((p) => (
            <label key={p} className="cursor-pointer">
              <input type="radio" value={p} {...form.register('priority')} className="sr-only peer" />
              <span className="block px-3 py-1.5 rounded-md border peer-checked:border-primary peer-checked:bg-primary/10 peer-checked:text-primary">
                {p}
              </span>
            </label>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={() => navigate('/tickets')}
          className="px-4 py-2 rounded-md border text-sm hover:bg-muted"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={submitMutation.isPending || attachments.uploading || inlinePending > 0}
          className="px-5 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 disabled:opacity-60"
        >
          {submitMutation.isPending ? 'Submitting…' : attachments.uploading || inlinePending > 0 ? 'Uploading…' : 'Submit Ticket'}
        </button>
      </div>
    </form>
  );
}

function formatProblem(body: unknown): string {
  if (typeof body === 'string') return body;
  if (body && typeof body === 'object' && 'errors' in body) {
    const errors = (body as { errors?: Record<string, string[]> }).errors;
    if (errors) {
      return Object.entries(errors)
        .flatMap(([, msgs]) => msgs)
        .join(' ');
    }
  }
  return 'The server rejected the submission. Please review and try again.';
}
